import {
  LuFileText, LuGift, LuGlobe, LuImage, LuInbox, LuMail, LuPalette, LuQrCode,
  LuChartColumn, LuListChecks, LuShieldCheck, LuSlidersHorizontal, LuSparkles, LuTarget, LuUsers, LuVideo,
} from 'react-icons/lu'
import { ImagesSection, ThemeSection } from './LookSections'
import { ChallengesSection, InfoSection, PrizeSection } from './ExperienceSections'
import { GuestsSection, ModerationSection, ReceptionSection, RecordingSection, StreamingSection, VideoAISection } from './VideoSections'
import { AdminsSection, EmailSection, GeneralSection, PlanSection, ShareSection } from './EventSections'

/**
 * Every settings tab of an event, in sidebar order, grouped as the sidebar
 * shows them. One entry decides everything about a tab:
 *
 * - `edit`: how its changes are kept.
 *     'draft' — fields of the event document, edited behind the tab's own
 *               Edit → Save (useEventDraft).
 *     'self'  — the section saves itself (video AI, templates, roles); the
 *               tab shows no Edit button.
 *     'none'  — nothing to edit.
 * - `visible(s)`: whether this event, this type and this person get the tab.
 *   The type's `hiddenSections` ids are the same ones the old page checked.
 * - `canEdit(s)`: for 'draft' tabs, whether the Edit button shows.
 * - `creatable`: also part of the create-event form. Tabs that act on an
 *   existing event (codes, roles, templates, the info sheet) are not.
 *
 * `s` is the section state built by EventWorkspaceContext / CreateEventForm.
 */
const isEventAdmin = (s) => s.isSysAdmin || s.eventRole === 'eventadmin'

export const SETTINGS_GROUPS = [
  {
    id: 'look',
    labelKey: 'workspace.groupLook',
    sections: [
      { id: 'images', labelKey: 'events.images', descKey: 'workspace.descImages', icon: LuImage, Component: ImagesSection, edit: 'draft', creatable: true },
      { id: 'theme', labelKey: 'events.themeColors', descKey: 'workspace.descTheme', icon: LuPalette, Component: ThemeSection, edit: 'draft', creatable: true,
        visible: (s) => !s.isHidden('themeColors') },
    ],
  },
  {
    id: 'experience',
    labelKey: 'workspace.groupExperience',
    sections: [
      { id: 'challenges', labelKey: 'challenges.title', descKey: 'workspace.descChallenges', icon: LuTarget, Component: ChallengesSection, edit: 'draft', creatable: true },
      { id: 'prize', labelKey: 'events.prizeMode', descKey: 'workspace.descPrize', icon: LuGift, Component: PrizeSection, edit: 'draft', creatable: true,
        visible: (s) => !s.isHidden('prizeMode') },
      { id: 'info', labelKey: 'workspace.tabInfo', descKey: 'events.infoSectionHelp', icon: LuFileText, Component: InfoSection, edit: 'draft' },
    ],
  },
  {
    id: 'videos',
    labelKey: 'workspace.groupVideos',
    sections: [
      { id: 'recording', labelKey: 'workspace.tabRecording', descKey: 'workspace.descRecording', icon: LuVideo, Component: RecordingSection, edit: 'draft', creatable: true },
      { id: 'reception', labelKey: 'workspace.tabReception', descKey: 'workspace.descReception', icon: LuInbox, Component: ReceptionSection, edit: 'draft', creatable: true },
      { id: 'moderation', labelKey: 'workspace.tabModeration', descKey: 'workspace.descModeration', icon: LuShieldCheck, Component: ModerationSection, edit: 'draft', creatable: true,
        visible: (s) => !s.isHidden('moderationSettings') },
      { id: 'ai', labelKey: 'workspace.tabAI', descKey: 'workspace.descAI', icon: LuSparkles, Component: VideoAISection, edit: 'self',
        visible: (s) => !s.isHidden('videoAI') },
      // Only a sysadmin can change streaming; everyone else sees it read-only.
      { id: 'streaming', labelKey: 'events.streamingAndGallery', descKey: 'workspace.descStreaming', icon: LuGlobe, Component: StreamingSection, edit: 'draft', creatable: true,
        canEdit: (s) => s.isSysAdmin },
      // Only types whose guests type a name on entry (weddings) have one to list.
      { id: 'guests', labelKey: 'workspace.tabGuests', descKey: 'workspace.descGuests', icon: LuListChecks, Component: GuestsSection, edit: 'none',
        visible: (s) => s.typeConfig?.entryFlow === 'nameDialog' },
    ],
  },
  {
    id: 'team',
    labelKey: 'workspace.groupTeam',
    sections: [
      { id: 'admins', labelKey: 'workspace.tabAdmins', descKey: 'workspace.descAdmins', icon: LuUsers, Component: AdminsSection, edit: 'self',
        visible: isEventAdmin },
      { id: 'email', labelKey: 'templates.eventTitle', descKey: 'templates.eventSubtitle', icon: LuMail, Component: EmailSection, edit: 'self',
        visible: isEventAdmin },
    ],
  },
  {
    id: 'event',
    labelKey: 'workspace.groupEvent',
    sections: [
      { id: 'general', labelKey: 'workspace.tabGeneral', descKey: 'workspace.descGeneral', icon: LuSlidersHorizontal, Component: GeneralSection, edit: 'draft' },
      { id: 'share', labelKey: 'workspace.tabShare', descKey: 'workspace.descShare', icon: LuQrCode, Component: ShareSection, edit: 'none' },
      { id: 'plan', labelKey: 'workspace.tabPlan', descKey: 'workspace.descPlan', icon: LuChartColumn, Component: PlanSection, edit: 'draft', creatable: true,
        visible: (s) => !s.isHidden('analytics') && s.isSysAdmin },
    ],
  },
]

const ALL_SECTIONS = SETTINGS_GROUPS.flatMap((g) => g.sections)

export function findSection(id) {
  return ALL_SECTIONS.find((sec) => sec.id === id) || null
}

export function isSectionVisible(section, s) {
  return !section.visible || section.visible(s)
}

// Whether this person gets the tab's Edit button.
export function canEditSection(section, s) {
  if (section.edit !== 'draft' || !s.canEdit) return false
  return !section.canEdit || section.canEdit(s)
}

// The groups and tabs this event and this person get, empty groups dropped.
export function visibleGroups(s, { creating = false } = {}) {
  return SETTINGS_GROUPS
    .map((g) => ({
      ...g,
      sections: g.sections.filter((sec) => isSectionVisible(sec, s) && (!creating || sec.creatable)),
    }))
    .filter((g) => g.sections.length > 0)
}
